import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import {
  CreateBannerRequestBody,
  DeleteBannerPath,
  GetBannersQuery,
  IBannerObject,
  TranslateBannerRequestBody,
  UpdateBannerPath,
  UpdateBannerRequestBody,
} from "../../interfaces/banner.interface";
import {
  createBanner,
  deleteBanner,
  listBanners,
  translateBanner,
  updateBanner,
} from "./bannersApi";

export interface BannersState {
  banners: IBannerObject[];
  total: number;
  isLoading: boolean;
  lastUpdatedKey: string;
}

const initialState: BannersState = {
  banners: [],
  total: 0,
  isLoading: false,
  lastUpdatedKey: "",
};

export const listBannersThunk = createAsyncThunk(
  "banners/list",
  async (query: GetBannersQuery) => {
    return listBanners(query);
  }
);

export const createBannerThunk = createAsyncThunk(
  "banners/create",
  async (body: CreateBannerRequestBody) => {
    return createBanner(body);
  }
);

export const updateBannerThunk = createAsyncThunk(
  "banners/update",
  async (args: { path: UpdateBannerPath; body: UpdateBannerRequestBody }) => {
    return updateBanner(args.path, args.body);
  }
);

export const deleteBannerThunk = createAsyncThunk(
  "banners/delete",
  async (path: DeleteBannerPath) => {
    await deleteBanner(path);
    return path.bannerId;
  }
);

export const translateBannerThunk = createAsyncThunk(
  "banners/translate",
  async (body: TranslateBannerRequestBody) => {
    return translateBanner(body);
  }
);

export const bannersSlice = createSlice({
  name: "banners",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(listBannersThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(listBannersThunk.fulfilled, (state, action) => {
        state.banners = action.payload.banners;
        state.total = action.payload.total;
        state.isLoading = false;
      })
      .addCase(listBannersThunk.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(createBannerThunk.fulfilled, (state, action) => {
        state.banners = [action.payload, ...state.banners];
        state.lastUpdatedKey = Date.now().toString();
      })
      .addCase(updateBannerThunk.fulfilled, (state, action) => {
        const updated = action.payload;
        state.banners = state.banners.map((b) =>
          b._id === updated._id ? updated : b
        );
        state.lastUpdatedKey = Date.now().toString();
      })
      .addCase(deleteBannerThunk.fulfilled, (state, action) => {
        const bannerId = action.payload;
        state.banners = state.banners.filter((b) => b._id !== bannerId);
        state.lastUpdatedKey = Date.now().toString();
      });
  },
});

export const selectBanners = (state: RootState) => state.banners.banners;
export const selectBannersLoading = (state: RootState) =>
  state.banners.isLoading;
export const selectBannersLastUpdatedKey = (state: RootState) =>
  state.banners.lastUpdatedKey;

export default bannersSlice.reducer;
